import { type FirebaseApp } from "firebase/app";
import type { ParsedToken } from "firebase/auth";
import { create } from "zustand";

import type { IUserClaims } from "@flowpos-workspace/global/interfaces/firebase.interfaces";

import { firebaseConfig } from "@/consts/client/firebase.consts";

// --- Non-hook access: singleton instance ---
let firebaseAppInstance: FirebaseApp | undefined;

// Helper function to check if we're in a browser environment
const isBrowser = () => {
	try {
		return (
			typeof window !== "undefined" &&
			typeof window.document !== "undefined" &&
			typeof window.document.createElement !== "undefined"
		);
	} catch {
		return false;
	}
};

// Helper function to safely initialize Firebase
const initializeFirebase = async () => {
	if (!isBrowser()) {
		return null;
	}

	try {
		// Dynamically import Firebase to avoid SSR issues
		const { initializeApp } = await import("firebase/app");
		return initializeApp(firebaseConfig);
	} catch (error) {
		console.error("Failed to initialize Firebase:", error);
		return null;
	}
};

export const getFirebaseAppInstance = async (): Promise<FirebaseApp> => {
	if (firebaseAppInstance) return firebaseAppInstance;

	const app = await initializeFirebase();
	if (app) {
		firebaseAppInstance = app;
		return app;
	}

	// Return a dummy app for server-side rendering
	return {} as FirebaseApp;
};

interface FirebaseStore {
	firebaseApp?: FirebaseApp;
	claims?: IUserClaims & ParsedToken;
	getFirebaseApp: () => Promise<FirebaseApp>;
	setClaims: (claims: IUserClaims & ParsedToken) => void;
}

// Create the store with proper typing
const firebaseStore = create<FirebaseStore>((set, get) => ({
	firebaseApp: undefined,
	claims: undefined,
	getFirebaseApp: async () => {
		const { firebaseApp } = get();

		if (firebaseApp) return firebaseApp;

		const app = await initializeFirebase();
		if (app) {
			set({ firebaseApp: app });
			return app;
		}

		// Return a dummy app for server-side rendering
		return {} as FirebaseApp;
	},
	setClaims: (claims: IUserClaims & ParsedToken) => set({ claims }),
}));

// Export the hook with proper typing
export const useFirebaseStore = firebaseStore;
